import { Breadcrumb, Col, Row, Button, MenuProps, Dropdown, message, Select } from "antd";
import { CaretDownOutlined } from "@ant-design/icons";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useMutation } from "react-query";
import InventoryBeginningList from "./InventoryBeginningList";
import ResetButton from "../UI/ResetButton/resetbutton";
import { Search } from "../UI/Search/Search";
import CancelBtn from "../UI/CancelButton/CancelBtn";
import { getInventoryBeginningByWarehouseId } from "../../api/inventoryBeginning";
import { InventoryItem } from "./Interfaces";
import FileDownloader from "./FileDownloader";
import FileUploader from "./FileUploader";
import { getCategory } from "../../api/category";

interface CategoryInterface {
  label: string;
  value: number
}

const InventoryBeginning = () => {
  const { t } = useTranslation("common");
  const [searchText, setSearchText] = useState("");
  const [inventoryBeginningItems, setInventoryBeginningItems] = useState<InventoryItem[]>([])
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null)
  const [categoryOptions, setCategoryOptions] = useState<CategoryInterface[]>([])
  const firstLoadRef = useRef(true);
  const [pagination, setPagination] = useState<{ current: number, pageSize: number, total: number, limit: number, totalPU: number, totalSA: number }>({
    current: 1,
    pageSize: 10,
    total: 0,
    limit: 10,
    totalPU: 0,
    totalSA: 0,
  });
  const baseURL = (window as any).REACT_APP_BASE_API_URL

  const { mutateAsync: getInventoryBeginningByWarehouseIdMutation, isLoading } = useMutation(
    "getInventoryBeginningByWarehouseId",
    ({ warhouseId, q, limit, offset, categoryId }: { warhouseId: number; q?: string, limit?: number, offset?: number, categoryId?: number | null }) =>
      getInventoryBeginningByWarehouseId({ warhouseId, q, limit, offset, categoryId }),
    {
      onSuccess: ({ data }) => {
        setInventoryBeginningItems(data.data);
        // for searching
        if (data.total !== pagination.total) {
          setPagination(prevPagination => ({
            ...prevPagination,
            total: data.total,
            current: 1,
            totalPU: data.totalPU,
            totalSA: data.totalSA
          }));
        }
      },
      onError: () => {
        message.error('cannot load data')
      }
    }
  );

  const { mutateAsync: getCategoryMutation, isLoading: isCategoryLoading } = useMutation(
    "getCategory",
    () =>
      getCategory(),
    {
      onSuccess: ({ data }) => {
        setCategoryOptions(data.map((item: any) => {
          return {
            label: item.name,
            value: item.id
          }
        }))
      },
      onError: () => {
        message.error('cannot load categories')
      }      
    }
  );

  const handleResetFilters = () => {
    if (!searchText && !selectedCategoryId && pagination.current === 1) return
    setSearchText("");
    setPagination(prevPagination => ({
      ...prevPagination,
      current: 1
    }))
    setSelectedCategoryId(null)
    getInventoryBeginningByWarehouseIdMutation({ warhouseId: 1, limit: pagination.limit });
  };

  const handleCategoryChange = (categoryId: number | null) => {
    setSelectedCategoryId(categoryId);
    getInventoryBeginningByWarehouseIdMutation({ warhouseId: 1, q: searchText, limit: pagination.limit, offset: 0, categoryId: categoryId })
  };

  const handleTableChange = (pagination: any) => {
    setPagination(pagination);
    getInventoryBeginningByWarehouseIdMutation({ warhouseId: 1, q: searchText, limit: pagination.limit, offset: (pagination.current - 1) * pagination.pageSize, categoryId: selectedCategoryId })
  };

  const handleSearch = (e: any) => {
    setSearchText(e.target.value)
  }

  useEffect(() => {
    // first time loading
    if (firstLoadRef.current) {
      getInventoryBeginningByWarehouseIdMutation({ warhouseId: 1, limit: pagination.limit });
      getCategoryMutation();
      firstLoadRef.current = false;
    } else {
      // on search
      const timeOutId = setTimeout(() => getInventoryBeginningByWarehouseIdMutation({ warhouseId: 1, q: searchText, categoryId: selectedCategoryId }), 500);
      return () => clearTimeout(timeOutId);
    }
  }, [searchText]);

  const items: MenuProps["items"] = [
    {
      label: (
        <FileDownloader fileName={'Sheet Iventory Beginning Details'} btnName={t('download')} link={baseURL + `/inventories/beginning/1/template`} />
      ),
      key: "1",
    },
    {
      label: (
        <FileUploader
          btnName={t('upload')}
          uploadUrl={baseURL + `/inventories/beginning/1/bulk-import?limit=${99999}`}
          inventoryBeginningItems={inventoryBeginningItems}
          setInventoryBeginningItems={setInventoryBeginningItems}

        />
      ),
      key: "2",
    },
  ];
  const menuProps = {
    items,
  };


  return (
    <>
      <Row gutter={[10, 10]} style={{ width: "100%" }}>
        <Col offset={1} span={11}>
          <Breadcrumb
            items={[
              {
                title: <Link to="/main">{t("home")}</Link>,
              },
              {
                title: <span >{t("inventory")}</span>,
              },
              {
                title: (
                  <span className="breadcrumbcolor">
                    {t("beginning")}
                  </span>
                ),
              },
            ]}
          />
        </Col>
        <Col className="editTitle" offset={1} span={22}>
        </Col>

        <Col offset={1} span={4}>
          <Search
            value={searchText}
            onChange={handleSearch}
          />
        </Col>

        <Col span={2}>
          <Select
            style={{ width: "110%" }}
            size="large"
            placeholder={t("category")}
            onChange={handleCategoryChange}
            value={selectedCategoryId !== null ? selectedCategoryId : undefined}
            loading={isCategoryLoading}
          >
            {categoryOptions.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
            <Select.Option key={null} value={""}>
              {t("")}
            </Select.Option>
          </Select>
        </Col>

        <Col
          style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}
          offset={14}
          span={2}
        >
          <ResetButton
            onClick={() => {
              handleResetFilters();
            }}
          />
          <Dropdown trigger={["click"]} menu={menuProps}>
            <Button
              type="primary"
              style={{ background: "#1677FF", color: "#fff", height: "40px" }}
            >
              <CaretDownOutlined />
              {t("action")}
            </Button>
          </Dropdown>
        </Col>
        <Col offset={1} span={22}>
          <InventoryBeginningList
            inventoryBeginningItems={inventoryBeginningItems}
            setInventoryBeginningItems={setInventoryBeginningItems}
            isLoading={isLoading}
            pagination={pagination}
            handleTableChange={handleTableChange}
          />
        </Col>
      </Row>
      <Row>
        <Col offset={1} style={{ paddingTop: "1rem" }}>
          <CancelBtn />
        </Col>
      </Row>
    </>
  );
};

export default InventoryBeginning;
